import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';

const formatDurationShort = (secs) => {
  if (!Number.isFinite(secs) || secs <= 0) return null;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const CalendarPage = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'es' ? 'es-ES' : 'en-US';

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await apiFetch("/api/sessions");
      if (response.ok) {
        setSessions(await response.json());
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const sessionsByDate = sessions.reduce((acc, session) => {
    const date = session.date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(session);
    return acc;
  }, {});

  const dates = Object.keys(sessionsByDate).sort().reverse();

  if (loading) {
    return <div className="loading"><div className="loading-spinner"></div></div>;
  }

  return (
    <div className="page-container animate-fade-in" data-testid="calendar-page">
      <div className="header">
        <h1 className="header-title">{t('calendar.title')}</h1>
        <Calendar size={32} color="var(--primary)" />
      </div>

      {dates.length === 0 ? (
        <div className="empty-state">
          <Calendar className="empty-icon" />
          <h3 className="empty-title">{t('calendar.no_sessions_title')}</h3>
          <p className="empty-desc">{t('calendar.no_sessions_desc')}</p>
        </div>
      ) : (
        dates.map((date) => (
          <div key={date} className="mb-4">
            <h3 style={{ fontSize: '14px', color: 'var(--muted-foreground)', marginBottom: '8px' }}>
              {new Date(date + 'T00:00:00').toLocaleDateString(dateLocale, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </h3>
            {sessionsByDate[date].map((session) => {
              const duration = formatDurationShort(session.duration_seconds);
              return (
                <div
                  key={session.session_id}
                  className="card mb-2"
                  style={{ padding: '12px', cursor: 'pointer' }}
                  onClick={() => navigate(`/session/${session.session_id}`)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontWeight: '600', color: 'var(--foreground)' }}>{session.workout_name}</p>
                      <p className="text-muted" style={{ fontSize: '12px' }}>{session.day_name}</p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px', flexShrink: 0 }}>
                      <span style={{ color: 'var(--primary)', fontSize: '14px', fontWeight: '600' }}>
                        {t('calendar.exercises_count', { count: session.exercises?.length || 0 })}
                      </span>
                      {duration && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--muted-foreground)', fontFamily: "'JetBrains Mono', monospace" }}>
                          <Clock size={11} />
                          {duration}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
};

export default CalendarPage;
